import { trackInAnalyticsRemoveFromCart, trackPiwikCartUpdate } from '@ifixit/analytics';
import { useTranslations } from '@ifixit/i18n/hooks';
import { useShopifyStorefrontClient } from '@ifixit/shopify-storefront-client';
import { useIsMutating, useMutation, useQueryClient } from '@tanstack/react-query';
import { convertCartLineItemsToAnalyticsItem } from '../../helpers/analytics';
import { getCart, removeCartLines } from '../../helpers/storefront-api';
import { buildIfixitCart } from '../../models/cart';
import { CartMutationType, type Cart, type CartLineItem } from '../../types';
import { useCart, useCartToasts } from './use-cart';
import { useCartKeys } from './use-cart-keys';
import { sendReactNativeMessage } from '@ifixit/utils';

export type RemoveLineItemInput = {
   line: CartLineItem;
   analytics: {
      localeCode: string;
   };
};

export function useRemoveLineItem() {
   const { mutationKey, queryKey } = useCartKeys();
   const cart = useCart().data;
   const isMutating = useIsMutating({
      mutationKey,
   });
   const queryClient = useQueryClient();
   const {
      client: storefrontClient,
      country,
      currencyCode,
      language,
   } = useShopifyStorefrontClient();
   const t = useTranslations();
   const { showCartWarnings, showErrorToast } = useCartToasts({
      title: t('ErrorHandling.unableToRemoveProduct'),
   });
   const mutation = useMutation({
      mutationKey,
      meta: { type: CartMutationType.RemoveLineItem },
      mutationFn: async ({ line }: RemoveLineItemInput) => {
         if (cart == null || !cart.shopifyCartId || !line?.shopifyLineId) {
            return buildIfixitCart({
               cart: null,
               fallbackCurrencyCode: currencyCode,
               userErrors: [],
               validationErrors: [],
               warnings: [],
            });
         }
         const result = await removeCartLines(storefrontClient, {
            cartId: cart.shopifyCartId,
            country,
            lineIds: [line.shopifyLineId],
            language,
         });
         const newCart =
            result.cart ??
            (result.userErrors.length > 0 || result.validationErrors.length > 0
               ? await getCart(storefrontClient, {
                    cartId: cart.shopifyCartId,
                    country,
                    language,
                 })
               : null);
         return buildIfixitCart({
            cart: newCart,
            fallbackCurrencyCode: currencyCode,
            userErrors: result.userErrors,
            validationErrors: result.validationErrors,
            warnings: result.warnings,
         });
      },
      onMutate: async (input: RemoveLineItemInput) => {
         await queryClient.cancelQueries({ queryKey });

         const previousCart = queryClient.getQueryData<Cart>(queryKey);

         queryClient.setQueryData<Cart | undefined>(queryKey, current => {
            if (current == null) {
               return current;
            }

            const deletedItem = current.lineItems.find(
               item => item.itemcode === input.line.itemcode
            );
            if (deletedItem == null) {
               return current;
            }
            const updatedItemCount = Math.max(current.totals.itemsCount - deletedItem.quantity, 0);
            const updateTotalPrice = Math.max(
               Number(current.totals.price.amount) -
                  deletedItem.quantity * Number(deletedItem.price.amount),
               0
            ).toFixed(2);
            return {
               ...current,
               hasItemsInCart: updatedItemCount > 0,
               isEmpty: updatedItemCount === 0,
               lineItems: current.lineItems.filter(
                  product => product.itemcode !== input.line.itemcode
               ),
               totals: {
                  ...current.totals,
                  price: {
                     ...current.totals.price,
                     amount: updateTotalPrice,
                  },
                  itemsCount: updatedItemCount,
               },
            };
         });

         return { previousCart };
      },
      onError: (error, variables, context) => {
         queryClient.setQueryData<Cart | undefined>(queryKey, context?.previousCart);
         showErrorToast();
      },
      onSuccess: (cart, variables) => {
         // Only update the cart state if we are the only mutation running.
         if (queryClient.isMutating({ mutationKey }) === 1) {
            queryClient.setQueryData<Cart | undefined>(queryKey, cart);
         }
         const { line, analytics } = variables;
         showCartWarnings({
            cart,
            type: CartMutationType.RemoveLineItem,
            variables,
         });

         trackInAnalyticsRemoveFromCart({
            items: convertCartLineItemsToAnalyticsItem([line]),
            value: line.quantity * Number(line.price.amount),
            currency: line.price.currencyCode,
            localeCode: analytics.localeCode,
            shopifyCartId: cart.shopifyCartId,
         });
         trackPiwikCartUpdate({
            items: convertCartLineItemsToAnalyticsItem(cart.lineItems),
            value: Number(cart.totals.price.amount),
            currency: cart.totals.price.currencyCode,
            localeCode: analytics.localeCode,
            shopifyCartId: cart.shopifyCartId,
         });
         sendReactNativeMessage({ type: 'cart_updated', total: cart.totals.itemsCount });
      },
   });
   return { removeLineItem: mutation, enabled: cart != null && !isMutating };
}
